import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeedbackApiBase } from './feedback-api-base';
import {
  DataEnvelope,
  EmailLogEntry,
  EmailPreferences,
  Paginated,
  PagesReadyForReviewRequest,
  PagesReadyForReviewResult,
  UpdateEmailPreferencesRequest,
} from '../models';

/**
 * Mirrors App\Http\Controllers\EmailController — a project's emails: the sent-email
 * log, the manual "Pages Ready for Review" email, and a stakeholder's email
 * preferences. All endpoints live under `/api/v1`.
 */
@Injectable({ providedIn: 'root' })
export class EmailService extends FeedbackApiBase {
  /** GET /v1/feedbackrounds/{id}/emails — the project's email log, newest first. */
  listEmails(feedbackRoundId: string, page = 1, perPage = 20): Observable<Paginated<EmailLogEntry>> {
    return this.http
      .get<DataEnvelope<Paginated<EmailLogEntry>>>(this.url(`feedbackrounds/${feedbackRoundId}/emails`), {
        params: this.buildParams({ page, perPage }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/emails/pages-ready-for-review/check — what sending
   * would do, without sending: the pages, blocking sites, and each recipient's outcome.
   */
  checkPagesReadyForReview(
    feedbackRoundId: string,
    request: PagesReadyForReviewRequest,
  ): Observable<PagesReadyForReviewResult> {
    return this.http
      .post<DataEnvelope<PagesReadyForReviewResult>>(
        this.url(`feedbackrounds/${feedbackRoundId}/emails/pages-ready-for-review/check`),
        request,
      )
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/emails/pages-ready-for-review — sends it. Fails with
   * 422 (carrying the check result) when there are no pages in review, a site blocks
   * it, or no recipient is left.
   */
  sendPagesReadyForReview(
    feedbackRoundId: string,
    request: PagesReadyForReviewRequest,
  ): Observable<PagesReadyForReviewResult> {
    return this.http
      .post<DataEnvelope<PagesReadyForReviewResult>>(
        this.url(`feedbackrounds/${feedbackRoundId}/emails/pages-ready-for-review`),
        request,
      )
      .pipe(map((response) => response.data));
  }

  /** GET /v1/feedbackrounds/{id}/email-preferences?email=… */
  getEmailPreferences(feedbackRoundId: string, email: string): Observable<EmailPreferences> {
    return this.http
      .get<DataEnvelope<EmailPreferences>>(this.url(`feedbackrounds/${feedbackRoundId}/email-preferences`), {
        params: this.buildParams({ email }),
      })
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/email-preferences */
  updateEmailPreferences(
    feedbackRoundId: string,
    request: UpdateEmailPreferencesRequest,
  ): Observable<EmailPreferences> {
    return this.http
      .put<DataEnvelope<EmailPreferences>>(
        this.url(`feedbackrounds/${feedbackRoundId}/email-preferences`),
        request,
      )
      .pipe(map((response) => response.data));
  }
}
